import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { PackageCheck, Search, Store, Truck } from 'lucide-react';
import ExportButton from '../../components/common/ExportButton';
import FilterSelect from '../../components/common/FilterSelect';
import useDebouncedValue from '../../hooks/useDebouncedValue';
import { logisticsOrdersAPI } from '../../services/api';
import { useAuthStore } from '../../store/authStore';
import { useStoreContextStore } from '../../store/storeContextStore';
import { downloadExcelWorkbook } from '../../utils/exportExcel';

const today = () => new Date().toISOString().split('T')[0];
const shiftDate = (value, days) => {
  const date = new Date(`${value || today()}T00:00:00`);
  date.setDate(date.getDate() + days);
  return date.toISOString().split('T')[0];
};
const formatDate = (value) => {
  if (!value) return '-';
  return new Date(value).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};
const COMPARISON_FILTER_OPTIONS = [
  { value: 'all', label: 'All' },
  { value: 'matched', label: 'Matched' },
  { value: 'unmatched', label: 'Unmatched' },
  { value: 'pending', label: 'Pending' },
];
const ORDER_TYPE_FILTER_OPTIONS = [
  { value: 'all', label: 'All Types' },
  { value: 'planned', label: 'Planned' },
  { value: 'unplanned', label: 'Unplanned' },
];
const SEARCH_DEBOUNCE_MS = 800;
const comparisonBadgeClass = (value) => ({
  matched: 'badge-success',
  unmatched: 'badge-danger',
  pending: 'badge-warning',
}[value] || 'badge-info');
const getComparisonStatus = (left, right) => {
  if (left === '' || left === null || left === undefined) return 'pending';
  if (right === '' || right === null || right === undefined) return 'pending';
  const leftValue = Number(left);
  const rightValue = Number(right);
  if (!Number.isFinite(leftValue) || !Number.isFinite(rightValue)) return 'pending';
  return leftValue === rightValue ? 'matched' : 'unmatched';
};
const orderTypeLabel = (value) => (String(value || 'planned').toLowerCase() === 'unplanned' ? 'Unplanned' : 'Planned');
const orderTypeBadgeClass = (value) => (String(value || 'planned').toLowerCase() === 'unplanned' ? 'badge-warning' : 'badge-info');
const buildSearchText = (row = {}) => [
  row.orderNumber,
  row.type,
  row.storeName,
  row.seller,
  row.sku,
  row.product,
].join(' ').toLowerCase();
const GoodsReceivedReport = () => {
  const { hasFeatureAccess } = useAuthStore();
  const selectedStore = useStoreContextStore((state) => state.selectedStore);
  const canViewPage = hasFeatureAccess('goods_received.report');
  const selectedStoreId = selectedStore?.storeInfoId || selectedStore?.store_info_id || selectedStore?.legacyStoreId || selectedStore?.legacy_store_id || '';
  const [rows, setRows] = useState([]);
  const [, setMeta] = useState({});
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);
  const [typeFilter, setTypeFilter] = useState('all');
  const [fromDate, setFromDate] = useState(shiftDate(today(), -30));
  const [toDate, setToDate] = useState(today());
  const [orderedVsDeliveredFilter, setOrderedVsDeliveredFilter] = useState('all');
  const [deliveredVsReceivedFilter, setDeliveredVsReceivedFilter] = useState('all');
  const [orderedVsReceivedFilter, setOrderedVsReceivedFilter] = useState('all');

  useEffect(() => {
    if (!canViewPage) return;

    const loadReport = async () => {
      if (!selectedStoreId) {
        setRows([]);
        setMeta({});
        return;
      }
      setLoading(true);
      try {
        const response = await logisticsOrdersAPI.getGoodsReceivedReport({
          search: debouncedSearch,
          type: typeFilter,
          storeId: selectedStoreId,
          fromDate,
          toDate,
        });
        setRows(response.data?.data || []);
        setMeta(response.data?.meta || {});
      } catch (error) {
        toast.error(error?.response?.data?.message || 'Failed to load goods received report');
      } finally {
        setLoading(false);
      }
    };

    loadReport();
  }, [canViewPage, debouncedSearch, fromDate, selectedStoreId, toDate, typeFilter]);

  const filteredRows = useMemo(() => {
    const searchQuery = String(debouncedSearch || '').trim().toLowerCase();

    return rows.filter((row) => {
      const orderedDeliveredStatus = getComparisonStatus(row.deliveredQuantity, row.orderQuantity);
      const deliveredReceivedStatus = getComparisonStatus(row.receivedQuantity, row.deliveredQuantity);
      const orderedReceivedStatus = getComparisonStatus(row.receivedQuantity, row.orderQuantity);

      if (searchQuery && !buildSearchText(row).includes(searchQuery)) return false;
      if (typeFilter !== 'all' && String(row.type || 'planned').toLowerCase() !== typeFilter) return false;
      if (orderedVsDeliveredFilter !== 'all' && orderedDeliveredStatus !== orderedVsDeliveredFilter) return false;
      if (deliveredVsReceivedFilter !== 'all' && deliveredReceivedStatus !== deliveredVsReceivedFilter) return false;
      if (orderedVsReceivedFilter !== 'all' && orderedReceivedStatus !== orderedVsReceivedFilter) return false;
      return true;
    });
  }, [debouncedSearch, deliveredVsReceivedFilter, orderedVsDeliveredFilter, orderedVsReceivedFilter, rows, typeFilter]);

  const comparisonSummary = useMemo(() => {
    const summarize = (getStatus) => filteredRows.reduce((accumulator, row) => {
      const status = getStatus(row);
      if (status === 'matched') accumulator.matched += 1;
      if (status === 'unmatched') accumulator.unmatched += 1;
      return accumulator;
    }, { matched: 0, unmatched: 0 });

    return {
      orderedVsDelivered: summarize((row) => getComparisonStatus(row.deliveredQuantity, row.orderQuantity)),
      orderedVsReceived: summarize((row) => getComparisonStatus(row.receivedQuantity, row.orderQuantity)),
      deliveredVsReceived: summarize((row) => getComparisonStatus(row.receivedQuantity, row.deliveredQuantity)),
    };
  }, [filteredRows]);

  const exportExcel = () => {
    downloadExcelWorkbook({
      fileName: `goods-received-report-${fromDate}-to-${toDate}.xlsx`,
      sheetName: 'Goods Received',
      columns: [
        { header: 'Order Number', key: 'orderNumber', width: 18 },
        { header: 'Type', width: 14, value: (row) => orderTypeLabel(row.type) },
        { header: 'Order Date', key: 'orderDate', width: 14 },
        { header: 'Store', key: 'storeName', width: 20 },
        { header: 'Seller', key: 'seller', width: 22 },
        { header: 'Delivered On', key: 'deliveredAt', width: 16 },
        { header: 'SKU', key: 'sku', width: 18 },
        { header: 'Product', key: 'product', width: 34 },
        { header: 'Ordered Qty', key: 'orderQuantity', width: 14 },
        { header: 'Delivered Qty', key: 'deliveredQuantity', width: 14 },
        { header: 'Ordered vs Delivered Diff', key: 'orderedVsDeliveredDifference', width: 22 },
        { header: 'Ordered vs Delivered Status', width: 24, value: (row) => getComparisonStatus(row.deliveredQuantity, row.orderQuantity) },
        { header: 'Received Qty', key: 'receivedQuantity', width: 14 },
        { header: 'Received vs Ordered Diff', key: 'receivedVsOrderedDifference', width: 22 },
        { header: 'Ordered vs Received Status', width: 24, value: (row) => getComparisonStatus(row.receivedQuantity, row.orderQuantity) },
        { header: 'Received vs Delivered Diff', key: 'receivedVsDeliveredDifference', width: 22 },
        { header: 'Delivered vs Received Status', width: 25, value: (row) => getComparisonStatus(row.receivedQuantity, row.deliveredQuantity) },
        { header: 'Updated By', width: 18, value: (row) => row.receivedUpdatedByName || '' },
      ],
      rows: filteredRows,
    });
  };

  if (!canViewPage) {
    return (
      <div className="space-y-6">
        <h1 className="text-2xl font-bold text-gray-900">Goods Received Report</h1>
        <div className="card">
          <p className="text-gray-500">Access denied.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Goods Received Report</h1>
          <p className="mt-1 text-sm text-gray-600">
            Review ordered, seller-delivered, and store-received quantities for every delivered logistics product line, planned or unplanned.
          </p>
        </div>
        <ExportButton onClick={exportExcel} />
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-4">
        <div className="card relative overflow-hidden border border-slate-200 bg-[radial-gradient(circle_at_top_left,_rgba(148,163,184,0.18),_transparent_42%),linear-gradient(135deg,_#f8fafc_0%,_#ffffff_48%,_#f1f5f9_100%)] p-4">
          <div className="pointer-events-none absolute inset-0 opacity-40" style={{ backgroundImage: 'radial-gradient(rgba(148,163,184,0.28) 0.7px, transparent 0.7px)', backgroundSize: '12px 12px' }} />
          <div className="relative flex items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="inline-flex h-10 w-10 items-center justify-center rounded-lg border border-slate-200 bg-white/80 shadow-sm backdrop-blur">
                <PackageCheck className="h-4.5 w-4.5 text-slate-700" />
              </div>
              <div className="space-y-1">
                <p className="text-xs font-semibold uppercase tracking-[0.22em] text-slate-500">Inventory Coverage</p>
                <p className="text-sm font-medium text-slate-700">Product Rows</p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-3xl font-black leading-none tracking-tight text-slate-900">{filteredRows.length}</p>
              <p className="mt-1 text-xs text-slate-500">Rows in current report range</p>
            </div>
          </div>
          <div className="relative mt-3 flex items-center gap-2">
            <span className="inline-flex items-center rounded-full border border-slate-200 bg-white/85 px-2.5 py-1 text-[11px] font-medium text-slate-600 shadow-sm">
              Search + filters applied
            </span>
            <span className="h-2 w-2 rounded-full bg-slate-300" />
            <span className="text-[11px] text-slate-500">Live summary</span>
          </div>
        </div>
        <div className="card relative overflow-hidden border border-slate-200 bg-[radial-gradient(circle_at_top_left,_rgba(148,163,184,0.18),_transparent_42%),linear-gradient(135deg,_#f8fafc_0%,_#ffffff_48%,_#f1f5f9_100%)]">
          <div className="pointer-events-none absolute inset-0 opacity-40" style={{ backgroundImage: 'radial-gradient(rgba(148,163,184,0.28) 0.7px, transparent 0.7px)', backgroundSize: '12px 12px' }} />
          <div className="relative space-y-4">
            <div className="flex items-center gap-3">
              <Truck className="h-6 w-6 text-gray-600" />
              <div>
                <p className="text-sm font-medium text-gray-700">Ordered vs Delivered</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg border border-success-200 bg-success-50 px-3 py-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-success-700">Matched</p>
                <p className="mt-1 text-xl font-bold text-success-800">{comparisonSummary.orderedVsDelivered.matched}</p>
              </div>
              <div className="rounded-lg border border-danger-200 bg-danger-50 px-3 py-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-danger-700">Unmatched</p>
                <p className="mt-1 text-xl font-bold text-danger-800">{comparisonSummary.orderedVsDelivered.unmatched}</p>
              </div>
            </div>
          </div>
        </div>
        <div className="card relative overflow-hidden border border-slate-200 bg-[radial-gradient(circle_at_top_left,_rgba(148,163,184,0.18),_transparent_42%),linear-gradient(135deg,_#f8fafc_0%,_#ffffff_48%,_#f1f5f9_100%)]">
          <div className="pointer-events-none absolute inset-0 opacity-40" style={{ backgroundImage: 'radial-gradient(rgba(148,163,184,0.28) 0.7px, transparent 0.7px)', backgroundSize: '12px 12px' }} />
          <div className="relative space-y-4">
            <div className="flex items-center gap-3">
              <Store className="h-6 w-6 text-gray-600" />
              <div>
                <p className="text-sm font-medium text-gray-700">Ordered vs Received</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg border border-success-200 bg-success-50 px-3 py-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-success-700">Matched</p>
                <p className="mt-1 text-xl font-bold text-success-800">{comparisonSummary.orderedVsReceived.matched}</p>
              </div>
              <div className="rounded-lg border border-danger-200 bg-danger-50 px-3 py-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-danger-700">Unmatched</p>
                <p className="mt-1 text-xl font-bold text-danger-800">{comparisonSummary.orderedVsReceived.unmatched}</p>
              </div>
            </div>
          </div>
        </div>
        <div className="card relative overflow-hidden border border-slate-200 bg-[radial-gradient(circle_at_top_left,_rgba(148,163,184,0.18),_transparent_42%),linear-gradient(135deg,_#f8fafc_0%,_#ffffff_48%,_#f1f5f9_100%)]">
          <div className="pointer-events-none absolute inset-0 opacity-40" style={{ backgroundImage: 'radial-gradient(rgba(148,163,184,0.28) 0.7px, transparent 0.7px)', backgroundSize: '12px 12px' }} />
          <div className="relative space-y-4">
            <div className="flex items-center gap-3">
              <Store className="h-6 w-6 text-gray-600" />
              <div>
                <p className="text-sm font-medium text-gray-700">Delivered vs Received</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-lg border border-success-200 bg-success-50 px-3 py-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-success-700">Matched</p>
                <p className="mt-1 text-xl font-bold text-success-800">{comparisonSummary.deliveredVsReceived.matched}</p>
              </div>
              <div className="rounded-lg border border-danger-200 bg-danger-50 px-3 py-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-danger-700">Unmatched</p>
                <p className="mt-1 text-xl font-bold text-danger-800">{comparisonSummary.deliveredVsReceived.unmatched}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="flex flex-wrap items-end gap-3">
          <label className="relative block min-w-[185px] flex-[0.95_1_215px]">
            <span className="label">Search</span>
            <Search className="pointer-events-none absolute left-3 top-[2.55rem] h-4 w-4 text-gray-400" />
            <input
              className="input pl-10"
              placeholder="Order, store, SKU, product..."
              value={search}
              onChange={(event) => setSearch(event.target.value)}
            />
          </label>

          <FilterSelect
            label="Type"
            value={typeFilter}
            options={ORDER_TYPE_FILTER_OPTIONS}
            onChange={setTypeFilter}
            className="w-[128px]"
          />
          <FilterSelect
            label="Ordered vs Delivered"
            value={orderedVsDeliveredFilter}
            options={COMPARISON_FILTER_OPTIONS}
            onChange={setOrderedVsDeliveredFilter}
            className="w-[142px]"
          />
          <FilterSelect
            label="Delivered vs Received"
            value={deliveredVsReceivedFilter}
            options={COMPARISON_FILTER_OPTIONS}
            onChange={setDeliveredVsReceivedFilter}
            className="w-[142px]"
          />
          <FilterSelect
            label="Ordered vs Received"
            value={orderedVsReceivedFilter}
            options={COMPARISON_FILTER_OPTIONS}
            onChange={setOrderedVsReceivedFilter}
            className="w-[142px]"
          />

          <div className="w-[120px]">
            <label className="label">Date From</label>
            <input className="input text-sm" type="date" value={fromDate} max={toDate} onChange={(event) => setFromDate(event.target.value)} />
          </div>

          <div className="w-[120px]">
            <label className="label">Date To</label>
            <input className="input text-sm" type="date" value={toDate} min={fromDate} max={today()} onChange={(event) => setToDate(event.target.value)} />
          </div>
        </div>
      </div>

      <div className="card table-scroll-shell">
        <table className="min-w-[1680px] divide-y divide-gray-200 text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-gray-500">
              <th className="px-3 py-3 font-semibold">Order Number</th>
              <th className="px-3 py-3 font-semibold">Type</th>
              <th className="px-3 py-3 font-semibold">Order Date</th>
              <th className="px-3 py-3 font-semibold">Store / Seller</th>
              <th className="px-3 py-3 font-semibold">Delivered On</th>
              <th className="px-3 py-3 font-semibold">SKU</th>
              <th className="px-3 py-3 font-semibold">Product</th>
              <th className="px-3 py-3 font-semibold">Ordered Qty</th>
              <th className="px-3 py-3 font-semibold">Delivered Qty</th>
              <th className="px-3 py-3 font-semibold">Ordered vs Delivered</th>
              <th className="px-3 py-3 font-semibold">Ordered vs Delivered Status</th>
              <th className="px-3 py-3 font-semibold">Received Qty</th>
              <th className="px-3 py-3 font-semibold">Received vs Ordered</th>
              <th className="px-3 py-3 font-semibold">Ordered vs Received Status</th>
              <th className="px-3 py-3 font-semibold">Received vs Delivered</th>
              <th className="px-3 py-3 font-semibold">Delivered vs Received Status</th>
              <th className="px-3 py-3 font-semibold">Updated By</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {filteredRows.map((row) => (
              <tr key={`${row.orderId}-${row.sku}-${row.product}`}>
                <td className="px-3 py-3">
                  <div className="font-semibold text-gray-900">{row.orderNumber}</div>
                  <div className="mt-2">
                    <span className={`badge ${orderTypeBadgeClass(row.type)}`}>{orderTypeLabel(row.type)}</span>
                  </div>
                </td>
                <td className="px-3 py-3 text-gray-700">{orderTypeLabel(row.type)}</td>
                <td className="px-3 py-3 text-gray-700">{formatDate(row.orderDate)}</td>
                <td className="px-3 py-3 text-gray-700">
                  <div>{row.storeName}</div>
                  <div className="mt-1 text-xs text-gray-500">{row.seller}</div>
                </td>
                <td className="px-3 py-3 text-gray-700">{formatDate(row.deliveredAt)}</td>
                <td className="px-3 py-3 font-medium text-gray-800">{row.sku || '-'}</td>
                <td className="px-3 py-3 text-gray-700">{row.product || '-'}</td>
                <td className="px-3 py-3 text-gray-700">{row.orderQuantity ?? '-'}</td>
                <td className="px-3 py-3 text-gray-700">{row.deliveredQuantity ?? '-'}</td>
                <td className={`px-3 py-3 ${row.orderedVsDeliveredDifference < 0 ? 'text-warning-700' : row.orderedVsDeliveredDifference > 0 ? 'text-danger-700' : 'text-gray-700'}`}>
                  {row.orderedVsDeliveredDifference ?? '-'}
                </td>
                <td className="px-3 py-3">
                  <span className={`badge ${comparisonBadgeClass(getComparisonStatus(row.deliveredQuantity, row.orderQuantity))}`}>
                    {getComparisonStatus(row.deliveredQuantity, row.orderQuantity)}
                  </span>
                </td>
                <td className="px-3 py-3 text-gray-700">{row.receivedQuantity ?? '-'}</td>
                <td className={`px-3 py-3 ${row.receivedVsOrderedDifference < 0 ? 'text-warning-700' : row.receivedVsOrderedDifference > 0 ? 'text-danger-700' : 'text-gray-700'}`}>
                  {row.receivedVsOrderedDifference ?? '-'}
                </td>
                <td className="px-3 py-3">
                  <span className={`badge ${comparisonBadgeClass(getComparisonStatus(row.receivedQuantity, row.orderQuantity))}`}>
                    {getComparisonStatus(row.receivedQuantity, row.orderQuantity)}
                  </span>
                </td>
                <td className={`px-3 py-3 ${row.receivedVsDeliveredDifference < 0 ? 'text-warning-700' : row.receivedVsDeliveredDifference > 0 ? 'text-danger-700' : 'text-gray-700'}`}>
                  {row.receivedVsDeliveredDifference ?? '-'}
                </td>
                <td className="px-3 py-3">
                  <span className={`badge ${comparisonBadgeClass(getComparisonStatus(row.receivedQuantity, row.deliveredQuantity))}`}>
                    {getComparisonStatus(row.receivedQuantity, row.deliveredQuantity)}
                  </span>
                </td>
                <td className="px-3 py-3 text-gray-500">{row.receivedUpdatedByName || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>

        {!loading && filteredRows.length === 0 ? (
          <div className="px-4 py-10 text-center text-sm text-gray-500">
            No goods received rows found for the selected filters.
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default GoodsReceivedReport;
